/**
 * Uniswap V4 Universal Router swap utility.
 * Used for chains not supported by the Trading API (e.g. Base Sepolia).
 *
 * Flow for ETH → USDC:
 *   1. Quote (Quoter contract)
 *   2. Build V4Planner actions: SWAP_EXACT_IN_SINGLE → SETTLE_ALL → TAKE_ALL
 *   3. Encode via RoutePlanner → Universal Router execute (with msg.value = amountIn)
 *
 * Flow for USDC → ETH:
 *   1. Quote
 *   2. Ensure Permit2 approvals (ERC20 → Permit2, Permit2 → UniversalRouter)
 *   3. Same swap encoding (no msg.value)
 */

import { V4Planner, Actions } from "@uniswap/v4-sdk";
import { RoutePlanner, CommandType } from "@uniswap/universal-router-sdk";
import { encodeFunctionData, decodeFunctionResult, decodeErrorResult } from "viem";
import {
  USDC_BY_CHAIN,
  PERMIT2_ADDRESS,
  UNIVERSAL_ROUTER_BY_CHAIN,
  V4_QUOTER_BY_CHAIN,
} from "./constants";

// Native ETH is represented as address(0) in Uniswap V4
const NATIVE_ETH = "0x0000000000000000000000000000000000000000";

// ─── Minimal ABIs ────────────────────────────────────────────────────────────

const ERC20_ABI = [
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
];

const PERMIT2_ABI = [
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "user", type: "address" },
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
      { name: "nonce", type: "uint48" },
    ],
  },
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "token", type: "address" },
      { name: "spender", type: "address" },
      { name: "amount", type: "uint160" },
      { name: "expiration", type: "uint48" },
    ],
    outputs: [],
  },
];

const QUOTER_ABI = [
  {
    name: "quoteExactInputSingle",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          {
            name: "poolKey",
            type: "tuple",
            components: [
              { name: "currency0", type: "address" },
              { name: "currency1", type: "address" },
              { name: "fee", type: "uint24" },
              { name: "tickSpacing", type: "int24" },
              { name: "hooks", type: "address" },
            ],
          },
          { name: "zeroForOne", type: "bool" },
          { name: "exactAmount", type: "uint128" },
          { name: "sqrtPriceLimitX96", type: "uint160" },
          { name: "hookData", type: "bytes" },
        ],
      },
    ],
    outputs: [
      { name: "amountOut", type: "uint256" },
      { name: "gasEstimate", type: "uint256" },
    ],
  },
];

const UNIVERSAL_ROUTER_ABI = [
  {
    name: "execute",
    type: "function",
    stateMutability: "payable",
    inputs: [
      { name: "commands", type: "bytes" },
      { name: "inputs", type: "bytes[]" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function ethCall(to, data, provider, value = "0x0", gasLimit = 5_000_000) {
  return provider.request({
    method: "eth_call",
    params: [
      {
        to,
        data,
        value,
        gas: "0x" + gasLimit.toString(16),
      },
      "latest",
    ],
  });
}

// Simulate a transaction with eth_call and throw a human-readable error if it reverts.
async function simulateTx(txParams, provider) {
  try {
    await provider.request({
      method: "eth_call",
      params: [
        {
          from: txParams.from,
          to: txParams.to,
          data: txParams.data,
          value: txParams.value || "0x0",
          gas: txParams.gas || "0x989680",
        },
        "latest",
      ],
    });
  } catch (e) {
    const hexReason = e?.data?.data ?? e?.data ?? "";
    if (typeof hexReason === "string" && hexReason.startsWith("0x")) {
      if (hexReason.startsWith("0x08c379a0")) {
        try {
          const msg = decodeErrorResult({ data: hexReason });
          throw new Error(`Simulation failed: ${msg}`);
        } catch (_) { /* fall through */ }
      }
      throw new Error(`Simulation reverted: ${hexReason}`);
    }
    throw new Error(`Simulation reverted: ${e?.message ?? String(e)}`);
  }
}

async function sendTx(params, provider) {
  return provider.request({
    method: "eth_sendTransaction",
    params: [params],
  });
}

// ─── Quote — tries multiple fee tiers to find a live pool ───────────────────

// Common V4 fee tiers: [fee, tickSpacing]
const FEE_TIERS = [
  [500, 10],
  [3000, 60],
  [10000, 200],
  [100, 1],
];

let resolvedFee = null; // cache the working fee tier per session

async function getV4Quote({ quoterAddress, basePoolKey, zeroForOne, amountMinor, provider }) {
  const tiers = resolvedFee ? [resolvedFee] : FEE_TIERS;
  for (const [fee, tickSpacing] of tiers) {
    const poolKey = { ...basePoolKey, fee, tickSpacing };
    const calldata = encodeFunctionData({
      abi: QUOTER_ABI,
      functionName: "quoteExactInputSingle",
      args: [
        {
          poolKey,
          zeroForOne,
          exactAmount: BigInt(amountMinor),
          sqrtPriceLimitX96: 0n,
          hookData: "0x",
        },
      ],
    });
    try {
      const resultHex = await ethCall(quoterAddress, calldata, provider);
      const [amountOut] = decodeFunctionResult({
        abi: QUOTER_ABI,
        functionName: "quoteExactInputSingle",
        data: resultHex,
      });
      resolvedFee = [fee, tickSpacing]; // cache for future swaps
      console.log(`[V4] Pool found: fee=${fee}, tickSpacing=${tickSpacing}, amountOut=${amountOut}`);
      return { amountOut, poolKey };
    } catch (_) {
      console.log(`[V4] No pool at fee=${fee}/tickSpacing=${tickSpacing}`);
    }
  }
  throw new Error(
    `No Uniswap V4 ETH/USDC pool found. ` +
    `Tried fee tiers: ${FEE_TIERS.map(([f]) => f).join(", ")}.`,
  );
}

// ─── Permit2 approvals (only needed for ERC20 input, i.e. USDC → ETH) ───────

async function ensurePermit2Approvals({
  walletAddress,
  tokenAddress,
  routerAddress,
  amountMinor,
  waitForTx,
  setStatus,
  provider,
}) {
  // 1. Check ERC20 allowance for Permit2
  const allowanceCalldata = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: "allowance",
    args: [walletAddress, PERMIT2_ADDRESS],
  });
  const allowanceHex = await ethCall(tokenAddress, allowanceCalldata, provider);
  const [currentAllowance] = decodeFunctionResult({
    abi: ERC20_ABI,
    functionName: "allowance",
    data: allowanceHex,
  });

  if (currentAllowance < BigInt(amountMinor)) {
    setStatus("Approving USDC for Permit2...");
    const approveTxData = encodeFunctionData({
      abi: ERC20_ABI,
      functionName: "approve",
      args: [PERMIT2_ADDRESS, 2n ** 256n - 1n],
    });
    const approveTxHash = await sendTx({
      from: walletAddress,
      to: tokenAddress,
      data: approveTxData,
    }, provider);
    await waitForTx(approveTxHash);
    setStatus("✓ USDC approved for Permit2");
  }

  // 2. Check Permit2 allowance for Universal Router
  const p2AllowanceCalldata = encodeFunctionData({
    abi: PERMIT2_ABI,
    functionName: "allowance",
    args: [walletAddress, tokenAddress, routerAddress],
  });
  const p2AllowanceHex = await ethCall(PERMIT2_ADDRESS, p2AllowanceCalldata, provider);
  const [p2Amount, p2Expiration] = decodeFunctionResult({
    abi: PERMIT2_ABI,
    functionName: "allowance",
    data: p2AllowanceHex,
  });

  const nowSec = Math.floor(Date.now() / 1000);
  if (p2Amount < BigInt(amountMinor) || Number(p2Expiration) < nowSec) {
    setStatus("Approving Universal Router on Permit2...");
    const twoYears = nowSec + 60 * 60 * 24 * 730;
    const p2ApproveTxData = encodeFunctionData({
      abi: PERMIT2_ABI,
      functionName: "approve",
      args: [tokenAddress, routerAddress, 2n ** 160n - 1n, twoYears],
    });
    const p2ApproveTxHash = await sendTx({
      from: walletAddress,
      to: PERMIT2_ADDRESS,
      data: p2ApproveTxData,
    }, provider);
    await waitForTx(p2ApproveTxHash);
    setStatus("✓ Universal Router approved on Permit2");
  }
}

// ─── Main V4 swap executor ───────────────────────────────────────────────────

/**
 * Read-only price quote using the on-chain V4 Quoter contract (no tx sent).
 *
 * @param {object} opts
 * @param {string} opts.amountMinor  input amount in base units (string)
 * @param {"ethToUsdc"|"usdcToEth"} opts.direction
 * @param {number} opts.swapChainId
 * @param {object} opts.provider     EIP-1193 provider
 * @returns {Promise<{amountOut: bigint, poolKey: object, fee: number}>}
 */
export async function quoteV4Swap({ amountMinor, direction, swapChainId, provider }) {
  const usdcAddress = USDC_BY_CHAIN[swapChainId];
  const quoterAddress = V4_QUOTER_BY_CHAIN[swapChainId];
  if (!usdcAddress || !quoterAddress) {
    throw new Error(`V4 swap not configured for chain ${swapChainId}`);
  }
  const basePoolKey = {
    currency0: NATIVE_ETH,
    currency1: usdcAddress,
    fee: 100,
    tickSpacing: 10,
    hooks: NATIVE_ETH,
  };
  const zeroForOne = direction === "ethToUsdc";
  const { amountOut, poolKey } = await getV4Quote({
    quoterAddress,
    basePoolKey,
    zeroForOne,
    amountMinor,
    provider,
  });
  return { amountOut, poolKey, fee: poolKey.fee };
}

/**
 * @param {object} opts
 * @param {string} opts.walletAddress
 * @param {"ethToUsdc"|"usdcToEth"} opts.direction
 * @param {string} opts.amountMinor   amount in token base units (string)
 * @param {number} opts.swapChainId
 * @param {Function} opts.setStatus   (message: string) => void
 * @param {Function} opts.waitForTx   (hash: string) => Promise<receipt>
 * @param {object}   opts.provider     EIP-1193 provider from the connected wallet
 * @returns {Promise<string>} tx hash
 */
export async function executeV4Swap({
  walletAddress,
  direction,
  amountMinor,
  swapChainId,
  setStatus,
  waitForTx,
  provider,
}) {
  const usdcAddress = USDC_BY_CHAIN[swapChainId];
  const routerAddress = UNIVERSAL_ROUTER_BY_CHAIN[swapChainId];
  const quoterAddress = V4_QUOTER_BY_CHAIN[swapChainId];

  if (!usdcAddress || !routerAddress) {
    throw new Error(`V4 swap not configured for chain ${swapChainId}`);
  }

  // In V4, native ETH (address(0)) is always currency0 (lowest sort order)
  // Base poolKey without fee — the Quoter will resolve which fee tier has liquidity
  const basePoolKey = {
    currency0: NATIVE_ETH,
    currency1: usdcAddress,
    fee: 100,       // placeholder; getV4Quote will try multiple tiers
    tickSpacing: 10,
    hooks: NATIVE_ETH, // no hooks
  };

  // ETH→USDC = currency0→currency1 = zeroForOne true
  // USDC→ETH = currency1→currency0 = zeroForOne false
  const zeroForOne = direction === "ethToUsdc";
  const currencyIn  = zeroForOne ? NATIVE_ETH : usdcAddress;
  const currencyOut = zeroForOne ? usdcAddress : NATIVE_ETH;

  // ── 1. Quote — discovers the correct fee tier ───────────────────────────────
  setStatus("Finding pool and quoting...");
  const amountInBig = BigInt(amountMinor);
  let amountOutMinBig = 0n;
  let poolKey = basePoolKey; // will be replaced with the resolved fee tier

  if (quoterAddress) {
    const { amountOut, poolKey: resolvedPoolKey } = await getV4Quote({
      quoterAddress,
      basePoolKey,
      zeroForOne,
      amountMinor,
      provider,
    });
    poolKey = resolvedPoolKey; // use the fee tier that has an actual pool
    amountOutMinBig = (amountOut * 95n) / 100n; // 5% slippage
    setStatus(`✓ Pool found (fee=${poolKey.fee}), quote received`);
  } else {
    throw new Error("No Quoter configured for this chain — cannot determine pool fee tier");
  }

  // ── 2. Permit2 approvals (USDC input only) ─────────────────────────────────
  if (direction === "usdcToEth") {
    setStatus("Checking token approvals...");
    await ensurePermit2Approvals({
      walletAddress,
      tokenAddress: usdcAddress,
      routerAddress,
      amountMinor,
      waitForTx,
      setStatus,
      provider,
    });
  }

  // ── 3. Build & send swap transaction ───────────────────────────────────────
  setStatus("Building V4 swap transaction...");

  const v4Planner = new V4Planner();

  // All numeric args must be BigInt for correct ABI encoding
  v4Planner.addAction(Actions.SWAP_EXACT_IN_SINGLE, [
    {
      poolKey,
      zeroForOne,
      amountIn: amountInBig,
      amountOutMinimum: amountOutMinBig,
      hookData: "0x",
    },
  ]);
  // SETTLE_ALL: pay the input currency (cap = exact amount in for EXACT_INPUT)
  v4Planner.addAction(Actions.SETTLE_ALL, [currencyIn, amountInBig]);
  // TAKE_ALL: receive the output currency (0n = accept whatever comes out)
  v4Planner.addAction(Actions.TAKE_ALL, [currencyOut, 0n]);

  const routePlanner = new RoutePlanner();
  routePlanner.addCommand(CommandType.V4_SWAP, [
    v4Planner.actions,
    v4Planner.params,
  ]);

  const deadline = BigInt(Math.floor(Date.now() / 1000) + 3600);

  const executeCalldata = encodeFunctionData({
    abi: UNIVERSAL_ROUTER_ABI,
    functionName: "execute",
    args: [routePlanner.commands, routePlanner.inputs, deadline],
  });

  const txParams = {
    from: walletAddress,
    to: routerAddress,
    data: executeCalldata,
    gas: "0x989680", // 10,000,000 gas
    ...(zeroForOne ? { value: "0x" + amountInBig.toString(16) } : {}),
  };

  // Simulate first so the user sees the actual revert reason before signing
  setStatus("Simulating swap...");
  await simulateTx(txParams, provider);
  setStatus("Simulation passed — requesting signature...");

  return await sendTx(txParams, provider);
}
